import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {ContactModel} from '@app/models/contact.model';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtAbsenceService} from '@app/services/gt-absence.service';
import {AbsenceEntry} from '@gt-api';
import * as moment from 'moment';
import {combineLatest, EMPTY, Observable, of, Subject, timer} from 'rxjs';
import {catchError, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {GtStatusModelService} from '@app/services/gt-status-model.service';
import {StatusModel} from '@app/models/status.model';
import {StatusAbsentModel} from '@app/models/status-absent.model';
import {StatusAvailableModel} from '@app/models/status-available.model';
import {absenceDuration} from '@app/models/status.constants';

export enum StatusComponentState {
  SELECT_ABSENCE_STATUS,
  CHANGE_DURATION,
  EDIT_STATUS_SETTINGS,
  CHOOSE_DURATION,
  CHOOSE_CUSTOM_DURATION
}

const POLL_EVERY_MINUTE = 60000;

@Component({
  selector: 'gt-status',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss']
})
export class StatusComponent implements OnInit, OnDestroy {
  @Input() orgname: string;
  @Input() login: string;
  @Input() canEdit = true;
  @Input() hasPolling = false;
  statusModels: Array<StatusModel>;
  endUser: ContactModel;
  interactiveStatusModels$ = (oname: string, login: string): Observable<StatusModel[]> => {
    return this.statusModelService
      .statusModels$(oname, login).pipe(
        map(statusModels => statusModels.filter(status => status.reasonId !== 100 && status.reasonId !== 101))
      );
  };
  activeStatusModel: StatusAbsentModel | StatusAvailableModel;
  tempStatusWhileChoosingDuration: StatusAbsentModel;
  currentState: StatusComponentState = StatusComponentState.SELECT_ABSENCE_STATUS;
  currentDateTime: moment.Moment;

  State = StatusComponentState;
  defaultDurations = absenceDuration;

  private destroyed = new Subject<void>();

  // This property is for handling the strict type checking of templates by cheating a little,
  // marginally better than casting to any. This can be improved, but it keeps the current
  // functionality intact without drastic changes.
  get activeStatusAsAbsentModel() {
    return this.activeStatusModel as StatusAbsentModel;
  }

  constructor(
    private readonly notificationService: GtNotificationService,
    private readonly absenceService: GtAbsenceService,
    private readonly directoryService: GtDirectoryService,
    private readonly authenticationService: GtAuthenticationService,
    private readonly statusModelService: GtStatusModelService
  ) {}

  ngOnInit() {
    this.orgname = this.orgname ? this.orgname : this.authenticationService.oname;
    this.login = this.login ? this.login : this.authenticationService.login;
    this.statusModelService.fetchStatusModels(this.orgname, this.login);

    this.absenceService.getAbsenceReasons(this.orgname, this.login)
      .pipe(
        switchMap(() => combineLatest([this.statusModelService.statusModels$(this.orgname, this.login), this.directoryService.getContacts(this.orgname, true)])),
        tap(([statusModels, contacts]) => {
          this.statusModels = statusModels;
          if (!this.endUser) {
            this.endUser = contacts?.find(c => c.login === this.login);
          }
          this.updateCurrentStatus(this.endUser);
        }),
        catchError(err => {
          this.error(err);
          return of();
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();

    if (this.hasPolling) {
      timer(0, POLL_EVERY_MINUTE)
        .pipe(
          tap(_ => this.refreshGetAbsenceEntries()),
          takeUntil(this.destroyed)
        )
        .subscribe();
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private error = error => {
    this.notificationService.showErrorCustomMessage(error);
  };

  private sendStatusToBackend(absenceReason: StatusAbsentModel) {
    this.absenceService.addAbsenceEntry(absenceReason.createAbsenceEntry(this.endUser.oname, this.endUser.login)).subscribe(
      absenceEntryId => {
        if (this.hasPolling) {
          this.endUser.absenceId = absenceEntryId;
          this.refreshGetAbsenceEntries();
        }
        // contactsStore should emit a new contactmodel with absenceEntry from push if that's enabled
      },
      this.error,
      () => this.changeState(StatusComponentState.SELECT_ABSENCE_STATUS)
    );
  }

  chooseDuration(duration: string) {
    this.absenceService
      .removeCurrentAbsentEntries(this.endUser.oname, this.endUser.login)
      .pipe(
        tap(() => {
          this.tempStatusWhileChoosingDuration.initializeWithDefaultValues();
          this.tempStatusWhileChoosingDuration.setTime(duration);
          this.sendStatusToBackend(this.tempStatusWhileChoosingDuration);
        }),
        catchError(err => {
          this.error(err);
          return EMPTY;
        })
      )
      .subscribe();
  }

  chooseCustomDuration() {
    this.absenceService
      .removeCurrentAbsentEntries(this.endUser.oname, this.endUser.login)
      .pipe(
        tap(() => {
          this.tempStatusWhileChoosingDuration.initializeWithDefaultValues();
          this.tempStatusWhileChoosingDuration.startTime = this.tempStatusWhileChoosingDuration.setStartTimeAsMoment().toDate();
          this.tempStatusWhileChoosingDuration.endTime = this.currentDateTime.toDate();
          this.sendStatusToBackend(this.tempStatusWhileChoosingDuration);
        }),
        catchError(err => {
          this.error(err);
          return EMPTY;
        })
      )
      .subscribe();
  }

  cancelChoosingDuration() {
    this.changeState(StatusComponentState.SELECT_ABSENCE_STATUS);
    this.tempStatusWhileChoosingDuration = undefined;
  }

  activate(activatedStatus: StatusModel): void {
    if (this.activeStatusModel && activatedStatus.reasonId === this.activeStatusModel.reasonId) {
      return;
    } else if (activatedStatus.reasonId !== 0 && (activatedStatus as StatusAbsentModel).defaultDuration == null) {
      this.tempStatusWhileChoosingDuration = activatedStatus as StatusAbsentModel;
      this.changeState(StatusComponentState.CHOOSE_DURATION);
      return;
    }
    this.absenceService.removeCurrentAbsentEntries(this.endUser.oname, this.endUser.login).subscribe(() => {
      this.endUser.absenceEntry = null;
      if (activatedStatus.reasonId !== 0) {
        const absentModel = activatedStatus as StatusAbsentModel;
        absentModel.initializeWithDefaultValues();
        absentModel.setTime(absentModel.defaultDuration);
        this.sendStatusToBackend(absentModel);
      } else {
        this.activeStatusModel = this.statusModels.find(r => r.reasonId === 0) as StatusAvailableModel;
      }
    }, this.error);
  }

  changeState(newState: StatusComponentState) {
    switch (newState) {
      case StatusComponentState.CHANGE_DURATION:
        this.currentDateTime = moment(this.activeStatusAsAbsentModel?.endTime);
        break;
      case StatusComponentState.CHOOSE_CUSTOM_DURATION:
        this.currentDateTime = moment(this.tempStatusWhileChoosingDuration?.endTime);
        break;
    }
    this.currentState = newState;
  }

  changeDuration() {
    this.activeStatusAsAbsentModel.endTime = this.currentDateTime.toDate();
    this.absenceService
      .editAbsenceEntry(
        this.activeStatusAsAbsentModel.createAbsenceEntry(this.orgname, this.login, this.activeStatusAsAbsentModel.absenceId)
      )
      .pipe(
        tap(() => this.changeState(StatusComponentState.SELECT_ABSENCE_STATUS)),
        catchError(err => {
          this.error(err);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private refreshGetAbsenceEntries() {
    this.absenceService.getAbsenceEntriesForUser(this.orgname, this.login).subscribe(res => {
      this.endUser.absenceEntry = res.find(a => a.id === this.endUser.absenceId);
      this.updateCurrentStatus(this.endUser);
    });
  }

  private updateCurrentStatus(contact: ContactModel) {
    if (contact) {
      this.setCurrentStatus(contact.absenceEntry);
    }
  }

  private setCurrentStatus(current: AbsenceEntry): void {
    if (!current) {
      this.activeStatusModel = this.statusModels.find(r => r.reasonId === 0) as StatusAvailableModel;
      return;
    }
    this.activeStatusModel = this.statusModels.find(e => current.reasonId === e.reasonId) as StatusAbsentModel;
    if (current.endTime) this.activeStatusModel.endTime = new Date(current.endTime);
    this.activeStatusModel.startTime = current.startTime ? current.startTime : new Date();
    this.activeStatusModel.reasonId = current.reasonId;
    this.activeStatusModel.absenceId = current.id;
    this.activeStatusModel.actionId = current.actionId;
    this.activeStatusModel.readReason = current.readReason;
    this.activeStatusModel.readTime = current.readTime;
    this.activeStatusModel.tempLogoutAgent = current.tempLogoutAgent;
    this.statusModelService.setForwardingNumber(this.activeStatusModel);
    if (current.appointmentInfo) {
      if (current.appointmentInfo.subject) {
        this.activeStatusModel.appointmentSubject = current.appointmentInfo.subject;
      }
    } else {
      this.activeStatusModel.appointmentSubject = undefined;
    }
  }
}
